import { LocalizableError } from '@pressreader/types';

import { localizedString, localizedStringWithDefault } from './utils';

/** Resource key of the site-wide wording for a failure that named no reason. */
const UNKNOWN_ERROR_RESOURCE_KEY = 'UnknownError';

export interface ErrorAlertContent {
    message: string;
    isHtml?: boolean;
}

/** Wording to show when an error carries no text of its own. */
export interface ErrorFallback {
    /** Resource key, resolved through the site-wide mechanism. */
    resourceKey: string;
    /** Text to show while the key is not registered in the resource bundles. */
    message?: string;
}

const UNKNOWN_ERROR_FALLBACK: ErrorFallback = { resourceKey: UNKNOWN_ERROR_RESOURCE_KEY };

interface NestedError {
    message?: string;
    resourceKey?: string;
}

/** Escape a dynamic value before it is spliced into HTML alert content. */
export function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Localize by resourceKey, falling back to the raw message when the key is missing or unresolved. */
function localize(resourceKey: string | undefined, message: string | undefined): string {
    return resourceKey ? localizedStringWithDefault(resourceKey, message ?? '') : message ?? '';
}

/**
 * The resource key a backend error names its reason with, if it names one.
 *
 * Reads the project error contract (`{ message, resourceKey }`, camelCase) either at the top level
 * or inside the `responseJSON` wrapper produced by `HttpError`; an empty key reads as no key, which
 * is what `HttpError` leaves behind for a response that named no reason. The key is the same in
 * every locale, unlike the wording it resolves to — which is what makes it the value to report to
 * analytics.
 */
export function getErrorResourceKey(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') {
        return undefined;
    }

    const obj = error as Record<string, unknown>;
    const responseJSON = obj['responseJSON'] as Record<string, unknown> | undefined;

    return ((obj['resourceKey'] ?? responseJSON?.['resourceKey']) as string | undefined) || undefined;
}

/** The reason an error names, localized: its key resolved, its message the default. Empty when it names neither. */
function localizeErrorHeader(error: unknown): string {
    if (!error || typeof error !== 'object') {
        return '';
    }

    const obj = error as Record<string, unknown>;
    const responseJSON = obj['responseJSON'] as Record<string, unknown> | undefined;
    const message = (obj['message'] ?? responseJSON?.['message']) as string | undefined;

    return localize(getErrorResourceKey(error), message);
}

/**
 * The error's reason as plain text — for the display paths that render into a text node and would
 * show markup as markup. Nested `errors[]` are left out for the same reason; use
 * `getErrorAlertContent` where an HTML list can be rendered.
 *
 * Plain strings pass through; a value that names no reason yields the localized "UnknownError".
 */
export function getErrorText(error: unknown): string {
    if (typeof error === 'string') {
        return error;
    }

    return localizeErrorHeader(error) || localizedString(UNKNOWN_ERROR_RESOURCE_KEY);
}

/**
 * Build alert content from a standard backend error value.
 *
 * Reads the project error contract (`{ message, resourceKey, errors? }`, camelCase)
 * either at the top level or inside the `responseJSON` wrapper produced by `HttpError`.
 * When `errors[]` is present, returns an HTML message: the top-level header followed by
 * a bulleted list of localized nested messages, with every dynamic value HTML-escaped.
 * Otherwise returns a plain-text single message. Plain strings pass through; empty input
 * yields the localized "UnknownError".
 */
export function getErrorAlertContent(error: unknown): ErrorAlertContent {
    if (typeof error === 'string') {
        return { message: error };
    }

    if (!error || typeof error !== 'object') {
        return { message: localizedString(UNKNOWN_ERROR_RESOURCE_KEY) };
    }

    const obj = error as Record<string, unknown>;
    const responseJSON = obj['responseJSON'] as Record<string, unknown> | undefined;

    const header = localizeErrorHeader(error);

    const rawErrors = obj['errors'] ?? responseJSON?.['errors'];
    const errorList = Array.isArray(rawErrors) ? (rawErrors as NestedError[]) : [];
    const items = errorList.map(entry => localize(entry?.resourceKey, entry?.message)).filter(Boolean);

    if (items.length === 0) {
        return { message: header || localizedString(UNKNOWN_ERROR_RESOURCE_KEY) };
    }

    const bulleted = items.map(item => `• ${escapeHtml(item)}`);
    const lines = header ? [escapeHtml(header), ...bulleted] : bulleted;

    return { message: lines.join('<br>'), isHtml: true };
}

function toFallbackError({ message, resourceKey }: ErrorFallback, cause: unknown): Error {
    return new LocalizableError(message ?? '', resourceKey, cause);
}

function carriesOwnText(error: Error): boolean {
    return Boolean(error.message || (error as Partial<LocalizableError>).resourceKey);
}

/**
 * The error to put in front of a user.
 *
 * A value that says nothing on its own — a message-less error, a rejected value that is not an
 * error at all — becomes the fallback, so the user never faces a blank alert or a stringified
 * object. Pass `fallback` when the caller's domain can name the failure more precisely than the
 * site-wide "unknown error"; omit it to get that site-wide wording.
 *
 * The result carries text and resource key, not resolved wording: every display path
 * (`getErrorAlertContent`, the Knockout alert handlers) resolves the key itself and falls back to
 * the text when the key is not registered.
 *
 * A replaced value is kept as `cause`, so what the failure actually was — an HTTP status, a
 * transport payload — is still reachable for the log.
 */
/**
 * Whether a failure carries wording that was written for the user.
 *
 * The resource key is what separates the two kinds of failure a flow catches: one its domain
 * phrased for a person and can localize, and a transport or programming failure whose text names
 * nothing anyone can act on. A caller shows the first as it is and substitutes its own wording for
 * the second — which is what keeps internal detail out of an alert without hard-coding a list of
 * messages that may reach one.
 */
export function carriesUserWording(error: unknown): error is Error & { resourceKey: string } {
    const candidate = error as Partial<LocalizableError> | undefined;

    return typeof candidate?.message === 'string' && Boolean(candidate.resourceKey);
}

export function toDisplayableError(error: unknown, fallback: ErrorFallback = UNKNOWN_ERROR_FALLBACK): Error {
    if (error instanceof Error) {
        return carriesOwnText(error) ? error : toFallbackError(fallback, error);
    }

    return typeof error === 'string' && error ? new Error(error) : toFallbackError(fallback, error);
}
